import type { Ref } from 'vue';
import { computed } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type { CreativesLinkTableDetailsItem } from '@modules/creatives-link/submodules/types';
import type { ITableDataResponse, ITableState } from '@modules/tables/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';
import { CREATIVES_LINK_PERMISSIONS } from '@modules/creatives-link/constants';
import {
  AFFILIATE_CREATIVES_LINK_TABLE_API_URL,
  AFFILIATE_CREATIVES_LINK_TABLE_QUERY_KEY,
} from '@modules/creatives-link/submodules/constants';

export default function useCreativeLinkListQuery(
  tableState: Ref<ITableState>,
  merchant: Ref<number | undefined>,
  serialId: Ref<string | undefined>,
  format: Ref<string | undefined>,
  language: Ref<string | undefined>,
  creativeAdvertisingCompanyId: Ref<string | undefined>,
  creativeResolutionId: Ref<string | undefined>,
): IContextQueryResult<ITableDataResponse<CreativesLinkTableDetailsItem>, Error> {
  const { fetchPost } = useRestClient();

  const params = computed(() => {
    const baseParams = {
      creative_advertising_company_id: creativeAdvertisingCompanyId.value,
      format: format.value,
      language: language.value,
      merchant: String(merchant.value),
      serial_id: serialId.value,
    };

    return !creativeResolutionId.value
      ? baseParams
      : {
          ...baseParams,
          creative_resolution_id: creativeResolutionId.value,
        };
  });

  return useContextQuery<ITableDataResponse<CreativesLinkTableDetailsItem>>({
    queryKey: [AFFILIATE_CREATIVES_LINK_TABLE_QUERY_KEY, tableState],
    queryFn: ({ signal }) => fetchPost<ITableDataResponse<CreativesLinkTableDetailsItem>>(
      AFFILIATE_CREATIVES_LINK_TABLE_API_URL,
      tableState.value,
      {
        signal,
        permission: CREATIVES_LINK_PERMISSIONS.SHOW,
        queryParams: params.value,
        urlParams: {
          formatType: format.value,
        },
      },
    ),
    enabled: computed(() => !!format.value),
  });
}
